/**
 * Página de gestión de SIMCE (Sistema de Medición de la Calidad de la Educación)
 *
 * Funcionalidades:
 * - Ver preguntas SIMCE
 * - Crear preguntas
 * - Editar preguntas
 * - Cargar preguntas (importar)
 * - Seleccionar preguntas para crear pruebas
 * - Filtrar por asignatura, nivel, dificultad
 * - Crear pruebas personalizadas
 */

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { alumnosProfesorService } from "../../../services/profesor";
import { simceService } from "../../../services/profesor/simce.service";
import { processTextForPDF } from "../../../utils/latexPdf";
import type { AlumnoProfesor } from "../alumnos/dto/alumnos.dto";

import { ProfesorLayout } from "../components";
import {
  AsignacionPruebaSimce,
  AsignarPruebaSimceDTO,
  ASIGNATURAS_LABELS,
  AsignaturaSimce,
  CrearPreguntaSimceDTO,
  CrearPruebaSimceDTO,
  DIFICULTAD_COLORS,
  DIFICULTAD_LABELS,
  DIFICULTADES_ORDENADAS,
  DificultadSimce,
  ESTADO_ASIGNACION_COLORS,
  ESTADO_ASIGNACION_LABELS,
  FiltrosPreguntasSimce,
  NIVELES_LABELS,
  NivelSimce,
  PreguntaSimce,
  PruebaSimce,
} from "./dto/simce.dto";
type VistaActiva =
  | "preguntas"
  | "crear-pregunta"
  | "crear-prueba"
  | "pruebas"
  | "asignaciones";

interface NotificationState {
  isOpen: boolean;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
}

export const SimcePage: React.FC = () => {
  useAuth(); // Mantener el hook aunque no usemos user por ahora
  const [vistaActiva, setVistaActiva] = useState<VistaActiva>("preguntas");
  const [filtros, setFiltros] = useState<FiltrosPreguntasSimce>({});
  const [busqueda, setBusqueda] = useState("");
  const [preguntasSeleccionadas, setPreguntasSeleccionadas] = useState<
    number[]
  >([]);
  const [preguntaEditando, setPreguntaEditando] =
    useState<PreguntaSimce | null>(null);
  const [preguntaViendo, setPreguntaViendo] = useState<PreguntaSimce | null>(
    null
  );
  const [pruebaViendo, setPruebaViendo] = useState<PruebaSimce | null>(null);
  const [pruebaAnterior, setPruebaAnterior] = useState<PruebaSimce | null>(
    null
  );
  const [preguntas, setPreguntas] = useState<PreguntaSimce[]>([]);
  const [pruebas, setPruebas] = useState<PruebaSimce[]>([]);
  const [asignaciones, setAsignaciones] = useState<AsignacionPruebaSimce[]>([]);
  const [asignacionesSeleccionadas, setAsignacionesSeleccionadas] = useState<
    number[]
  >([]);
  const [estudiantes, setEstudiantes] = useState<AlumnoProfesor[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAsignaciones, setLoadingAsignaciones] = useState(false);
  const [mostrarModalAsignar, setMostrarModalAsignar] = useState(false);
  const [pruebaSeleccionadaParaAsignar, setPruebaSeleccionadaParaAsignar] =
    useState<PruebaSimce | null>(null);
  const [estudiantesSeleccionados, setEstudiantesSeleccionados] = useState<
    number[]
  >([]);
  const [fechaLimiteAsignacion, setFechaLimiteAsignacion] = useState<
    string | null
  >(null);
  const [mostrarModalAgregarPreguntas, setMostrarModalAgregarPreguntas] =
    useState(false);
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  });
  const [confirmacionEliminar, setConfirmacionEliminar] = useState<{
    isOpen: boolean;
    tipo: "pregunta" | "prueba" | "asignacion" | "asignaciones";
    id: number | null;
    ids?: number[];
    nombre?: string;
    cantidad?: number;
  }>({
    isOpen: false,
    tipo: "pregunta",
    id: null,
  });

  // Cargar datos al iniciar y cuando cambien los filtros
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        const [preguntasData, pruebasData, estudiantesData] = await Promise.all(
          [
            simceService.getPreguntas(filtros),
            simceService.getPruebas(),
            alumnosProfesorService.getAlumnos(),
          ]
        );
        setPreguntas(preguntasData);
        setPruebas(pruebasData);
        setEstudiantes(estudiantesData);
      } catch (error: any) {
        console.error("Error cargando datos SIMCE:", error);
        showNotification(
          "error",
          "Error al cargar datos",
          error?.response?.data?.message || "No se pudieron cargar los datos"
        );
      } finally {
        setLoading(false);
      }
    };
    cargarDatos();
  }, [filtros]);

  // Cargar asignaciones cuando se cambie a la vista de asignaciones
  useEffect(() => {
    if (vistaActiva === "asignaciones") {
      cargarAsignaciones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vistaActiva]);

  const cargarAsignaciones = async () => {
    try {
      setLoadingAsignaciones(true);
      const asignacionesData = await simceService.getAsignaciones();
      setAsignaciones(asignacionesData);
    } catch (error: any) {
      console.error("Error cargando asignaciones:", error);
      showNotification(
        "error",
        "Error al cargar asignaciones",
        error?.response?.data?.message ||
          "No se pudieron cargar las asignaciones"
      );
    } finally {
      setLoadingAsignaciones(false);
    }
  };

  // Función para mostrar notificaciones
  const showNotification = (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message: string
  ) => {
    setNotification({ isOpen: true, type, title, message });
    setTimeout(() => {
      setNotification({ isOpen: false, type, title: "", message: "" });
    }, 4000);
  };

  // Estados para crear pregunta
  const [nuevaPregunta, setNuevaPregunta] = useState<CrearPreguntaSimceDTO>({
    contenido: "",
    asignatura: AsignaturaSimce.MATEMATICAS,
    nivel: NivelSimce.CUARTO_BASICO,
    dificultad: DificultadSimce.BASICA,
    alternativas: [
      { letra: "A", texto: "" },
      { letra: "B", texto: "" },
      { letra: "C", texto: "" },
      { letra: "D", texto: "" },
    ],
    respuestaCorrecta: "A",
    explicacion: "",
    tiempoEstimado: 3,
    ejeTematico: "",
    habilidad: "",
  });

  // Estados para crear prueba
  const [nuevaPrueba, setNuevaPrueba] = useState<CrearPruebaSimceDTO>({
    nombre: "",
    descripcion: "",
    asignatura: AsignaturaSimce.MATEMATICAS,
    nivel: NivelSimce.CUARTO_BASICO,
    preguntasIds: [],
  });

  // Filtrar preguntas
  const preguntasFiltradas = useMemo(() => {
    let resultado = [...preguntas];

    // Filtro por búsqueda
    if (busqueda) {
      const terminoLower = busqueda.toLowerCase();
      resultado = resultado.filter(
        (p) =>
          p.contenido.toLowerCase().includes(terminoLower) ||
          p.explicacion.toLowerCase().includes(terminoLower) ||
          p.ejeTematico?.toLowerCase().includes(terminoLower) ||
          p.habilidad?.toLowerCase().includes(terminoLower)
      );
    }

    // Filtros adicionales
    if (filtros.asignatura) {
      resultado = resultado.filter((p) => p.asignatura === filtros.asignatura);
    }
    if (filtros.nivel) {
      resultado = resultado.filter((p) => p.nivel === filtros.nivel);
    }
    if (filtros.dificultad) {
      resultado = resultado.filter((p) => p.dificultad === filtros.dificultad);
    }
    if (filtros.vigente !== undefined) {
      resultado = resultado.filter((p) => p.vigente === filtros.vigente);
    }

    return resultado;
  }, [filtros, busqueda, preguntas]);

  // Manejar filtros
  const handleFiltroChange = (key: keyof FiltrosPreguntasSimce, value: any) => {
    const nuevosFiltros = {
      ...filtros,
      [key]: value || undefined,
    };
    // Si es búsqueda, también actualizar el estado local
    if (key === "busqueda") {
      setBusqueda(value || "");
    }
    setFiltros(nuevosFiltros);
  };

  // Seleccionar/deseleccionar pregunta
  const toggleSeleccionPregunta = (id: number) => {
    setPreguntasSeleccionadas((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  // Seleccionar todas las preguntas visibles
  const seleccionarTodas = () => {
    const ids = preguntasFiltradas.map((p) => p.idPreguntaSimce);
    setPreguntasSeleccionadas(ids);
  };

  // Deseleccionar todas
  const deseleccionarTodas = () => {
    setPreguntasSeleccionadas([]);
  };

  // Abrir modal de crear/editar pregunta
  const abrirModalPregunta = (pregunta?: PreguntaSimce) => {
    if (pregunta) {
      setPreguntaEditando(pregunta);
      setNuevaPregunta({
        contenido: pregunta.contenido,
        asignatura: pregunta.asignatura,
        nivel: pregunta.nivel,
        dificultad: pregunta.dificultad,
        alternativas: pregunta.alternativas.map((a) => ({
          letra: a.letra,
          texto: a.texto,
        })),
        respuestaCorrecta:
          pregunta.alternativas.find((a) => a.esCorrecta)?.letra || "A",
        explicacion: pregunta.explicacion,
        tiempoEstimado: pregunta.tiempoEstimado,
        ejeTematico: pregunta.ejeTematico || "",
        habilidad: pregunta.habilidad || "",
      });
    } else {
      setPreguntaEditando(null);
      setNuevaPregunta({
        contenido: "",
        asignatura: AsignaturaSimce.MATEMATICAS,
        nivel: NivelSimce.CUARTO_BASICO,
        dificultad: DificultadSimce.BASICA,
        alternativas: [
          { letra: "A", texto: "" },
          { letra: "B", texto: "" },
          { letra: "C", texto: "" },
          { letra: "D", texto: "" },
        ],
        respuestaCorrecta: "A",
        explicacion: "",
        tiempoEstimado: 3,
        ejeTematico: "",
        habilidad: "",
      });
    }
    setVistaActiva("crear-pregunta");
  };

  // Guardar pregunta
  const guardarPregunta = async () => {
    // Validaciones
    if (!nuevaPregunta.contenido.trim()) {
      showNotification(
        "error",
        "Error de validación",
        "El contenido de la pregunta es requerido"
      );
      return;
    }

    if (nuevaPregunta.alternativas.some((alt) => !alt.texto.trim())) {
      showNotification(
        "error",
        "Error de validación",
        "Todas las alternativas deben tener texto"
      );
      return;
    }

    if (!nuevaPregunta.explicacion.trim()) {
      showNotification(
        "error",
        "Error de validación",
        "La explicación es requerida"
      );
      return;
    }

    try {
      if (preguntaEditando) {
        // Editar pregunta existente
        const preguntaActualizada = await simceService.updatePregunta({
          idPreguntaSimce: preguntaEditando.idPreguntaSimce,
          ...nuevaPregunta,
        });
        setPreguntas((prev) =>
          prev.map((p) =>
            p.idPreguntaSimce === preguntaEditando.idPreguntaSimce
              ? preguntaActualizada
              : p
          )
        );
        showNotification(
          "success",
          "Pregunta actualizada",
          "La pregunta se ha actualizado correctamente"
        );
      } else {
        // Crear nueva pregunta
        const nuevaPreguntaCompleta = await simceService.createPregunta(
          nuevaPregunta
        );
        setPreguntas((prev) => [...prev, nuevaPreguntaCompleta]);
        showNotification(
          "success",
          "Pregunta creada",
          "La pregunta se ha creado correctamente"
        );
      }
    } catch (error: any) {
      console.error("Error guardando pregunta:", error);
      showNotification(
        "error",
        "Error al guardar",
        error?.response?.data?.message || "No se pudo guardar la pregunta"
      );
      return;
    }

    setVistaActiva("preguntas");
    setPreguntaEditando(null);
    // Si había una prueba abierta antes de editar, restaurarla
    if (pruebaAnterior) {
      setPruebaViendo(pruebaAnterior);
      setPruebaAnterior(null);
    }
  };

  // Cambiar a vista de crear prueba
  const abrirCrearPrueba = () => {
    if (preguntasSeleccionadas.length === 0) {
      showNotification(
        "warning",
        "Selección requerida",
        "Selecciona al menos una pregunta para crear una prueba"
      );
      return;
    }
    setNuevaPrueba({
      nombre: "",
      descripcion: "",
      asignatura:
        preguntasFiltradas[0]?.asignatura || AsignaturaSimce.MATEMATICAS,
      nivel: preguntasFiltradas[0]?.nivel || NivelSimce.CUARTO_BASICO,
      preguntasIds: preguntasSeleccionadas,
    });
    setVistaActiva("crear-prueba");
  };

  // Guardar prueba
  const guardarPrueba = async () => {
    if (!nuevaPrueba.nombre.trim() || nuevaPrueba.preguntasIds.length === 0) {
      showNotification(
        "error",
        "Error de validación",
        "Completa todos los campos requeridos"
      );
      return;
    }

    try {
      const nuevaPruebaCompleta = await simceService.createPrueba(nuevaPrueba);
      setPruebas((prev) => [...prev, nuevaPruebaCompleta]);
      showNotification(
        "success",
        "Prueba creada",
        "La prueba se ha creado correctamente"
      );
      setVistaActiva("pruebas");
      setPreguntasSeleccionadas([]);
    } catch (error: any) {
      console.error("Error guardando prueba:", error);
      showNotification(
        "error",
        "Error al guardar",
        error?.response?.data?.message || "No se pudo guardar la prueba"
      );
    }
  };

  // Abrir modal de confirmación para eliminar pregunta
  const confirmarEliminarPregunta = (id: number) => {
    const pregunta = preguntas.find((p) => p.idPreguntaSimce === id);
    setConfirmacionEliminar({
      isOpen: true,
      tipo: "pregunta",
      id,
      nombre: pregunta?.contenido.substring(0, 50) + "...",
    });
  };

  // Abrir modal de confirmación para eliminar prueba
  const confirmarEliminarPrueba = (id: number) => {
    const prueba = pruebas.find((p) => p.idPruebaSimce === id);
    setConfirmacionEliminar({
      isOpen: true,
      tipo: "prueba",
      id,
      nombre: prueba?.nombre,
    });
  };

  // Abrir modal de confirmación para eliminar asignación
  const confirmarEliminarAsignacion = (id: number) => {
    const asignacion = asignaciones.find(
      (a) => a.idAsignacionPruebaSimce === id
    );
    setConfirmacionEliminar({
      isOpen: true,
      tipo: "asignacion",
      id,
      nombre: asignacion
        ? `${asignacion.estudiante.nombres} ${asignacion.estudiante.apellidos} - ${asignacion.prueba.nombre}`
        : undefined,
    });
  };

  // Abrir modal de confirmación para eliminar múltiples asignaciones
  const confirmarEliminarAsignaciones = (ids: number[]) => {
    setConfirmacionEliminar({
      isOpen: true,
      tipo: "asignaciones",
      id: null,
      ids,
      cantidad: ids.length,
    });
  };

  // Eliminar pregunta (confirmado)
  const eliminarPregunta = async () => {
    if (!confirmacionEliminar.id) return;
    try {
      await simceService.deletePregunta(confirmacionEliminar.id);
      setPreguntas((prev) =>
        prev.filter((p) => p.idPreguntaSimce !== confirmacionEliminar.id)
      );
      setConfirmacionEliminar({ isOpen: false, tipo: "pregunta", id: null });
      showNotification(
        "success",
        "Pregunta eliminada",
        "La pregunta se ha eliminado correctamente"
      );
    } catch (error: any) {
      console.error("Error eliminando pregunta:", error);
      showNotification(
        "error",
        "Error al eliminar",
        error?.response?.data?.message || "No se pudo eliminar la pregunta"
      );
    }
  };

  // Ver detalles de prueba
  const verDetallesPrueba = (prueba: PruebaSimce) => {
    setPruebaViendo(prueba);
  };

  // Eliminar asignación (confirmado)
  const eliminarAsignacion = async () => {
    if (!confirmacionEliminar.id) return;

    try {
      await simceService.deleteAsignacion(confirmacionEliminar.id);
      showNotification(
        "success",
        "Asignación eliminada",
        "La asignación se eliminó exitosamente"
      );
      cargarAsignaciones();
      setConfirmacionEliminar({
        isOpen: false,
        tipo: "pregunta",
        id: null,
      });
    } catch (error: any) {
      showNotification(
        "error",
        "Error al eliminar",
        error?.response?.data?.message || "No se pudo eliminar la asignación"
      );
    }
  };

  // Eliminar múltiples asignaciones (confirmado)
  const eliminarAsignaciones = async () => {
    if (!confirmacionEliminar.ids || confirmacionEliminar.ids.length === 0)
      return;

    try {
      await Promise.all(
        confirmacionEliminar.ids.map((id) => simceService.deleteAsignacion(id))
      );
      showNotification(
        "success",
        "Asignaciones eliminadas",
        `Se eliminaron ${confirmacionEliminar.ids.length} asignación(es) exitosamente`
      );
      setAsignacionesSeleccionadas([]);
      cargarAsignaciones();
      setConfirmacionEliminar({
        isOpen: false,
        tipo: "pregunta",
        id: null,
      });
    } catch (error: any) {
      showNotification(
        "error",
        "Error al eliminar",
        error?.response?.data?.message ||
          "No se pudieron eliminar algunas asignaciones"
      );
    }
  };

  // Eliminar prueba (confirmado)
  const eliminarPrueba = async () => {
    if (!confirmacionEliminar.id) return;
    try {
      await simceService.deletePrueba(confirmacionEliminar.id);
      setPruebas((prev) =>
        prev.filter((p) => p.idPruebaSimce !== confirmacionEliminar.id)
      );
      setConfirmacionEliminar({ isOpen: false, tipo: "prueba", id: null });
      // Si estaba viendo la prueba eliminada, cerrar el modal
      if (pruebaViendo?.idPruebaSimce === confirmacionEliminar.id) {
        setPruebaViendo(null);
        setPruebaAnterior(null);
      }
      showNotification(
        "success",
        "Prueba eliminada",
        "La prueba se ha eliminado correctamente"
      );
    } catch (error: any) {
      console.error("Error eliminando prueba:", error);
      showNotification(
        "error",
        "Error al eliminar",
        error?.response?.data?.message || "No se pudo eliminar la prueba"
      );
    }
  };

  // Descargar/Exportar prueba
  const descargarPrueba = async (prueba: PruebaSimce) => {
    try {
      // Obtener las preguntas de la prueba
      const preguntasDePrueba = preguntas.filter((p) =>
        prueba.preguntasIds.includes(p.idPreguntaSimce)
      );

      // Crear contenido del PDF usando jsPDF (similar a reportes)
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 20;

      // Header
      doc.setFillColor(139, 92, 246); // purple-600
      doc.rect(0, 0, pageWidth, 50, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("Prueba SIMCE", 20, 25);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(prueba.nombre, 20, 35);
      doc.text(`Tiempo total: ${prueba.tiempoTotal} minutos`, 20, 43);

      yPosition = 60;

      // Información de la prueba
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Información de la Prueba", 20, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Asignatura: ${ASIGNATURAS_LABELS[prueba.asignatura]}`,
        20,
        yPosition
      );
      yPosition += 7;
      doc.text(`Nivel: ${NIVELES_LABELS[prueba.nivel]}`, 20, yPosition);
      yPosition += 7;
      doc.text(
        `Número de preguntas: ${prueba.preguntasIds.length}`,
        20,
        yPosition
      );
      yPosition += 7;
      doc.text(`Tiempo estimado: ${prueba.tiempoTotal} minutos`, 20, yPosition);
      yPosition += 10;

      if (prueba.descripcion) {
        doc.text(`Descripción: ${prueba.descripcion}`, 20, yPosition);
        yPosition += 10;
      }

      yPosition += 5;

      // Preguntas
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Preguntas", 20, yPosition);
      yPosition += 10;

      // Procesar preguntas con LaTeX
      for (let index = 0; index < preguntasDePrueba.length; index++) {
        const pregunta = preguntasDePrueba[index];

        // Verificar si necesitamos una nueva página
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");

        // Procesar contenido de la pregunta con LaTeX
        const preguntaText = `${index + 1}. ${pregunta.contenido}`;
        const preguntaProcessed = await processTextForPDF(preguntaText);

        if (preguntaProcessed.hasLatex && preguntaProcessed.imageDataUrl) {
          // Si tiene LaTeX, agregar como imagen
          // Obtener dimensiones reales de la imagen
          const img = new Image();
          img.src = preguntaProcessed.imageDataUrl;
          await new Promise((resolve) => {
            img.onload = resolve;
          });

          const imgWidth = pageWidth - 40;
          const aspectRatio = img.height / img.width;
          const imgHeight = imgWidth * aspectRatio;

          doc.addImage(
            preguntaProcessed.imageDataUrl,
            "PNG",
            20,
            yPosition,
            imgWidth,
            imgHeight
          );
          yPosition += imgHeight + 5;
        } else {
          // Si no tiene LaTeX, usar texto normal
          const preguntaLines = doc.splitTextToSize(
            preguntaProcessed.plainText || preguntaText,
            pageWidth - 40
          );
          doc.text(preguntaLines, 20, yPosition);
          yPosition += preguntaLines.length * 6 + 3;
        }

        // Alternativas
        for (const alt of pregunta.alternativas) {
          const altText = `${alt.letra}) ${alt.texto}`;
          const altProcessed = await processTextForPDF(altText);

          if (altProcessed.hasLatex && altProcessed.imageDataUrl) {
            // Si tiene LaTeX, agregar como imagen
            // Obtener dimensiones reales de la imagen
            const img = new Image();
            img.src = altProcessed.imageDataUrl;
            await new Promise((resolve) => {
              img.onload = resolve;
            });

            const imgWidth = pageWidth - 50;
            const aspectRatio = img.height / img.width;
            const imgHeight = imgWidth * aspectRatio;

            doc.addImage(
              altProcessed.imageDataUrl,
              "PNG",
              30,
              yPosition,
              imgWidth,
              imgHeight
            );
            yPosition += imgHeight + 3;
          } else {
            // Si no tiene LaTeX, usar texto normal
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            const altLines = doc.splitTextToSize(
              altProcessed.plainText || altText,
              pageWidth - 50
            );
            doc.text(altLines, 30, yPosition);
            yPosition += altLines.length * 5 + 2;
          }
        }

        yPosition += 5;
      }

      // Guardar PDF
      const fileName = `Prueba_SIMCE_${prueba.nombre.replace(
        /[^a-z0-9]/gi,
        "_"
      )}_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);

      showNotification(
        "success",
        "Prueba descargada",
        "La prueba se ha descargado correctamente en formato PDF"
      );
    } catch (error) {
      console.error("Error al descargar prueba:", error);
      showNotification(
        "error",
        "Error al descargar",
        "No se pudo descargar la prueba. Por favor, inténtalo nuevamente."
      );
    }
  };

  // Ver detalles de pregunta
  const verDetallesPregunta = (pregunta: PreguntaSimce) => {
    setPreguntaViendo(pregunta);
  };

  // Renderizar vista de preguntas
  const renderVistaPreguntas = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Preguntas SIMCE</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona las preguntas para las pruebas SIMCE
          </p>
        </div>
        <button
          onClick={() => abrirModalPregunta()}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Crear Pregunta
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Búsqueda */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar
            </label>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por contenido, eje temático..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Asignatura */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asignatura
            </label>
            <select
              value={filtros.asignatura || ""}
              onChange={(e) =>
                handleFiltroChange("asignatura", e.target.value || undefined)
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Todas</option>
              {Object.entries(ASIGNATURAS_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Nivel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nivel
            </label>
            <select
              value={filtros.nivel || ""}
              onChange={(e) =>
                handleFiltroChange("nivel", e.target.value || undefined)
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              {Object.entries(NIVELES_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Dificultad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dificultad
            </label>
            <select
              value={filtros.dificultad || ""}
              onChange={(e) =>
                handleFiltroChange("dificultad", e.target.value || undefined)
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Todas</option>
              {DIFICULTADES_ORDENADAS.map((dificultad) => (
                <option key={dificultad} value={dificultad}>
                  {DIFICULTAD_LABELS[dificultad]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Botones de acción masiva */}
        {preguntasSeleccionadas.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {preguntasSeleccionadas.length} pregunta(s) seleccionada(s)
            </span>
            <button
              onClick={abrirCrearPrueba}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              Crear Prueba con Seleccionadas
            </button>
            <button
              onClick={deseleccionarTodas}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              Deseleccionar Todas
            </button>
          </div>
        )}
      </div>

      {/* Tabla de preguntas */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-purple-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    checked={
                      preguntasFiltradas.length > 0 &&
                      preguntasFiltradas.every((p) =>
                        preguntasSeleccionadas.includes(p.idPreguntaSimce)
                      )
                    }
                    onChange={(e) =>
                      e.target.checked
                        ? seleccionarTodas()
                        : deseleccionarTodas()
                    }
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                  Pregunta
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                  Asignatura
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                  Nivel
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                  Dificultad
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                  Tiempo
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Cargando preguntas...
                  </td>
                </tr>
              ) : preguntasFiltradas.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No se encontraron preguntas
                  </td>
                </tr>
              ) : (
                preguntasFiltradas.map((pregunta) => (
                  <tr
                    key={pregunta.idPreguntaSimce}
                    className={`hover:bg-purple-50 transition-colors ${
                      preguntasSeleccionadas.includes(pregunta.idPreguntaSimce)
                        ? "bg-purple-50"
                        : ""
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={preguntasSeleccionadas.includes(
                          pregunta.idPreguntaSimce
                        )}
                        onChange={() =>
                          toggleSeleccionPregunta(pregunta.idPreguntaSimce)
                        }
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-md">
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">
                          {pregunta.contenido}
                        </p>
                        {pregunta.ejeTematico && (
                          <p className="text-xs text-gray-500 mt-1">
                            {pregunta.ejeTematico}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {ASIGNATURAS_LABELS[pregunta.asignatura]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {NIVELES_LABELS[pregunta.nivel]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                          DIFICULTAD_COLORS[pregunta.dificultad]
                        }`}
                      >
                        {DIFICULTAD_LABELS[pregunta.dificultad]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {pregunta.tiempoEstimado} min
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => abrirModalPregunta(pregunta)}
                          className="text-purple-600 hover:text-purple-900 transition-colors"
                          title="Editar"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => verDetallesPregunta(pregunta)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Ver detalles"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() =>
                            confirmarEliminarPregunta(pregunta.idPreguntaSimce)
                          }
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Eliminar"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
          <p className="text-sm text-gray-600">Total Preguntas</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {preguntasFiltradas.length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
          <p className="text-sm text-gray-600">Seleccionadas</p>
          <p className="text-2xl font-bold text-purple-600 mt-1">
            {preguntasSeleccionadas.length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
          <p className="text-sm text-gray-600">Tiempo Total Estimado</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {preguntasFiltradas
              .filter((p) => preguntasSeleccionadas.includes(p.idPreguntaSimce))
              .reduce((acc, p) => acc + p.tiempoEstimado, 0)}{" "}
            min
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
          <p className="text-sm text-gray-600">Pruebas Creadas</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {pruebas.length}
          </p>
        </div>
      </div>
    </div>
  );

  // Renderizar vista de crear/editar pregunta
  const renderCrearPregunta = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {preguntaEditando ? "Editar Pregunta" : "Crear Nueva Pregunta"}
        </h2>
        <button
          onClick={() => {
            setVistaActiva("preguntas");
            setPreguntaEditando(null);
            // Si había una prueba abierta antes de editar, restaurarla
            if (pruebaAnterior) {
              setPruebaViendo(pruebaAnterior);
              setPruebaAnterior(null);
            }
          }}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancelar
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100 space-y-6">
        {/* Contenido de la pregunta */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contenido de la Pregunta *
          </label>
          <textarea
            value={nuevaPregunta.contenido}
            onChange={(e) =>
              setNuevaPregunta({ ...nuevaPregunta, contenido: e.target.value })
            }
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Escribe la pregunta aquí..."
          />
        </div>

        {/* Información básica */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asignatura *
            </label>
            <select
              value={nuevaPregunta.asignatura}
              onChange={(e) =>
                setNuevaPregunta({
                  ...nuevaPregunta,
                  asignatura: e.target.value as AsignaturaSimce,
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {Object.entries(ASIGNATURAS_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nivel *
            </label>
            <select
              value={nuevaPregunta.nivel}
              onChange={(e) =>
                setNuevaPregunta({
                  ...nuevaPregunta,
                  nivel: e.target.value as NivelSimce,
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {Object.entries(NIVELES_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dificultad *
            </label>
            <select
              value={nuevaPregunta.dificultad}
              onChange={(e) =>
                setNuevaPregunta({
                  ...nuevaPregunta,
                  dificultad: e.target.value as DificultadSimce,
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {DIFICULTADES_ORDENADAS.map((dificultad) => (
                <option key={dificultad} value={dificultad}>
                  {DIFICULTAD_LABELS[dificultad]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Alternativas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alternativas *
          </label>
          <div className="space-y-3">
            {nuevaPregunta.alternativas.map((alt, index) => (
              <div key={alt.letra} className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-100 text-purple-700 font-semibold">
                  {alt.letra}
                </span>
                <input
                  type="text"
                  value={alt.texto}
                  onChange={(e) => {
                    const nuevas = [...nuevaPregunta.alternativas];
                    nuevas[index].texto = e.target.value;
                    setNuevaPregunta({
                      ...nuevaPregunta,
                      alternativas: nuevas,
                    });
                  }}
                  placeholder={`Opción ${alt.letra}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <input
                  type="radio"
                  name="respuestaCorrecta"
                  checked={nuevaPregunta.respuestaCorrecta === alt.letra}
                  onChange={() =>
                    setNuevaPregunta({
                      ...nuevaPregunta,
                      respuestaCorrecta: alt.letra,
                    })
                  }
                  className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-600">Correcta</span>
              </div>
            ))}
          </div>
        </div>

        {/* Explicación */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Explicación *
          </label>
          <textarea
            value={nuevaPregunta.explicacion}
            onChange={(e) =>
              setNuevaPregunta({
                ...nuevaPregunta,
                explicacion: e.target.value,
              })
            }
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Explicación de la respuesta correcta..."
          />
        </div>

        {/* Información adicional */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tiempo Estimado (minutos) *
            </label>
            <input
              type="number"
              value={nuevaPregunta.tiempoEstimado}
              onChange={(e) =>
                setNuevaPregunta({
                  ...nuevaPregunta,
                  tiempoEstimado: parseInt(e.target.value) || 0,
                })
              }
              min={1}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Eje Temático
            </label>
            <input
              type="text"
              value={nuevaPregunta.ejeTematico}
              onChange={(e) =>
                setNuevaPregunta({
                  ...nuevaPregunta,
                  ejeTematico: e.target.value,
                })
              }
              placeholder="Ej: Números y operaciones"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Habilidad
            </label>
            <input
              type="text"
              value={nuevaPregunta.habilidad}
              onChange={(e) =>
                setNuevaPregunta({
                  ...nuevaPregunta,
                  habilidad: e.target.value,
                })
              }
              placeholder="Ej: Resolución de problemas"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              setVistaActiva("preguntas");
              setPreguntaEditando(null);
              // Si había una prueba abierta antes de editar, restaurarla
              if (pruebaAnterior) {
                setPruebaViendo(pruebaAnterior);
                setPruebaAnterior(null);
              }
            }}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={guardarPregunta}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg"
          >
            {preguntaEditando ? "Actualizar Pregunta" : "Crear Pregunta"}
          </button>
        </div>
      </div>
    </div>
  );

  // Renderizar vista de crear prueba
  const renderCrearPrueba = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          Crear Nueva Prueba SIMCE
        </h2>
        <button
          onClick={() => setVistaActiva("preguntas")}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancelar
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100 space-y-6">
        {/* Información básica */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nombre de la Prueba *
          </label>
          <input
            type="text"
            value={nuevaPrueba.nombre}
            onChange={(e) =>
              setNuevaPrueba({ ...nuevaPrueba, nombre: e.target.value })
            }
            placeholder="Ej: Prueba SIMCE Matemáticas 4to Básico - Enero 2024"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descripción
          </label>
          <textarea
            value={nuevaPrueba.descripcion}
            onChange={(e) =>
              setNuevaPrueba({ ...nuevaPrueba, descripcion: e.target.value })
            }
            rows={3}
            placeholder="Descripción de la prueba..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asignatura *
            </label>
            <select
              value={nuevaPrueba.asignatura}
              onChange={(e) =>
                setNuevaPrueba({
                  ...nuevaPrueba,
                  asignatura: e.target.value as AsignaturaSimce,
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {Object.entries(ASIGNATURAS_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nivel *
            </label>
            <select
              value={nuevaPrueba.nivel}
              onChange={(e) =>
                setNuevaPrueba({
                  ...nuevaPrueba,
                  nivel: e.target.value as NivelSimce,
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {Object.entries(NIVELES_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Preguntas seleccionadas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preguntas Seleccionadas ({nuevaPrueba.preguntasIds.length})
          </label>
          <div className="border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
            {nuevaPrueba.preguntasIds.length === 0 ? (
              <p className="text-gray-500 text-sm">
                No hay preguntas seleccionadas
              </p>
            ) : (
              <div className="space-y-2">
                {preguntasFiltradas
                  .filter((p) =>
                    nuevaPrueba.preguntasIds.includes(p.idPreguntaSimce)
                  )
                  .map((pregunta) => (
                    <div
                      key={pregunta.idPreguntaSimce}
                      className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {pregunta.contenido}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {DIFICULTAD_LABELS[pregunta.dificultad]} •{" "}
                          {pregunta.tiempoEstimado} min
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setNuevaPrueba({
                            ...nuevaPrueba,
                            preguntasIds: nuevaPrueba.preguntasIds.filter(
                              (id) => id !== pregunta.idPreguntaSimce
                            ),
                          });
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Tiempo total estimado:{" "}
            {preguntasFiltradas
              .filter((p) =>
                nuevaPrueba.preguntasIds.includes(p.idPreguntaSimce)
              )
              .reduce((acc, p) => acc + p.tiempoEstimado, 0)}{" "}
            minutos
          </p>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
          <button
            onClick={() => setVistaActiva("preguntas")}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={guardarPrueba}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg"
          >
            Crear Prueba
          </button>
        </div>
      </div>
    </div>
  );

  // Renderizar vista de pruebas
  const renderVistaPruebas = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Pruebas SIMCE Creadas
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Visualiza y gestiona las pruebas SIMCE creadas
          </p>
        </div>
        <button
          onClick={() => {
            setPreguntasSeleccionadas([]);
            setVistaActiva("preguntas");
          }}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Nueva Prueba
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full">
            <div className="bg-white rounded-xl shadow-lg p-12 border border-purple-100 text-center">
              <p className="text-gray-500">Cargando pruebas...</p>
            </div>
          </div>
        ) : pruebas.length === 0 ? (
          <div className="col-span-full">
            <div className="bg-white rounded-xl shadow-lg p-12 border border-purple-100 text-center">
              <div className="flex flex-col items-center justify-center">
                <svg
                  className="w-16 h-16 text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No hay pruebas creadas de momento
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Comienza creando una nueva prueba desde la sección de
                  preguntas
                </p>
                <button
                  onClick={() => {
                    setPreguntasSeleccionadas([]);
                    setVistaActiva("preguntas");
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg flex items-center gap-2 mx-auto"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Ir a Preguntas
                </button>
              </div>
            </div>
          </div>
        ) : (
          pruebas.map((prueba) => {
            return (
              <div
                key={prueba.idPruebaSimce}
                className="bg-white rounded-xl shadow-lg p-6 border border-purple-100 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {prueba.nombre}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {prueba.descripcion}
                    </p>
                  </div>
                  {prueba.vigente && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      Vigente
                    </span>
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Asignatura:</span>
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {ASIGNATURAS_LABELS[prueba.asignatura]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Nivel:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {NIVELES_LABELS[prueba.nivel]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Preguntas:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {prueba.preguntasIds.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Tiempo:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {prueba.tiempoTotal} minutos
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => verDetallesPrueba(prueba)}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    Ver Detalles
                  </button>
                  <button
                    onClick={() => abrirModalAsignar(prueba)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    title="Asignar a estudiantes"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => descargarPrueba(prueba)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    title="Descargar PDF"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() =>
                      confirmarEliminarPrueba(prueba.idPruebaSimce)
                    }
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    title="Eliminar prueba"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  // Funciones para asignaciones
  const abrirModalAsignar = (prueba: PruebaSimce) => {
    setPruebaSeleccionadaParaAsignar(prueba);
    setEstudiantesSeleccionados([]);
    setFechaLimiteAsignacion(null);
    setMostrarModalAsignar(true);
  };

  const asignarPrueba = async () => {
    if (
      !pruebaSeleccionadaParaAsignar ||
      estudiantesSeleccionados.length === 0
    ) {
      showNotification(
        "warning",
        "Datos incompletos",
        "Debes seleccionar al menos un estudiante"
      );
      return;
    }

    try {
      const data: AsignarPruebaSimceDTO = {
        idPruebaSimce: pruebaSeleccionadaParaAsignar.idPruebaSimce,
        estudianteIds: estudiantesSeleccionados,
        fechaLimite: fechaLimiteAsignacion || undefined,
      };

      await simceService.asignarPrueba(data);
      showNotification(
        "success",
        "Prueba asignada",
        `La prueba se asignó exitosamente a ${estudiantesSeleccionados.length} estudiante(s)`
      );
      setMostrarModalAsignar(false);
      setPruebaSeleccionadaParaAsignar(null);
      setEstudiantesSeleccionados([]);
      setFechaLimiteAsignacion(null);
      cargarAsignaciones();
    } catch (error: any) {
      console.error("Error asignando prueba:", error);
      showNotification(
        "error",
        "Error al asignar prueba",
        error?.response?.data?.message || "No se pudo asignar la prueba"
      );
    }
  };

  const generarPDFAsignacion = async (
    asignacion: AsignacionPruebaSimce,
    todas: boolean = false
  ) => {
    try {
      // Obtener el detalle completo de la asignación
      const asignacionDetalle = await simceService.getAsignacionById(
        asignacion.idAsignacionPruebaSimce
      );

      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 20;

      // Header con datos del estudiante
      doc.setFillColor(139, 92, 246); // purple-600
      doc.rect(0, 0, pageWidth, 60, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("Prueba SIMCE", 20, 25);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Estudiante: ${asignacionDetalle.estudiante.nombres} ${asignacionDetalle.estudiante.apellidos}`,
        20,
        35
      );
      if (asignacionDetalle.estudiante.rut) {
        doc.text(`RUT: ${asignacionDetalle.estudiante.rut}`, 20, 42);
      }
      if (asignacionDetalle.estudiante.cursos.length > 0) {
        const curso = asignacionDetalle.estudiante.cursos[0];
        doc.text(`Curso: ${curso.nombre}`, 20, 49);
        if (curso.colegio) {
          doc.text(`Colegio: ${curso.colegio}`, 20, 56);
        }
      }

      yPosition = 70;

      // Información de la prueba
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(asignacionDetalle.prueba.nombre, 20, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Asignatura: ${
          ASIGNATURAS_LABELS[asignacionDetalle.prueba.asignatura]
        }`,
        20,
        yPosition
      );
      yPosition += 7;
      doc.text(
        `Nivel: ${NIVELES_LABELS[asignacionDetalle.prueba.nivel]}`,
        20,
        yPosition
      );
      yPosition += 7;
      doc.text(
        `Tiempo total: ${asignacionDetalle.prueba.tiempoTotal} minutos`,
        20,
        yPosition
      );
      yPosition += 7;
      if (asignacionDetalle.fechaLimite) {
        const fechaLimite = new Date(asignacionDetalle.fechaLimite);
        doc.text(
          `Fecha límite: ${fechaLimite.toLocaleDateString("es-ES")}`,
          20,
          yPosition
        );
        yPosition += 7;
      }
      if (asignacionDetalle.asignador) {
        doc.text(
          `Asignado por: ${asignacionDetalle.asignador.nombres} ${asignacionDetalle.asignador.apellidos}`,
          20,
          yPosition
        );
        yPosition += 7;
      }
      yPosition += 5;

      // Preguntas
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Preguntas", 20, yPosition);
      yPosition += 10;

      asignacionDetalle.prueba.preguntas?.forEach((pregunta, index) => {
        // Verificar si necesitamos una nueva página
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        const preguntaText = `${index + 1}. ${pregunta.contenido}`;
        const preguntaLines = doc.splitTextToSize(preguntaText, pageWidth - 40);
        doc.text(preguntaLines, 20, yPosition);
        yPosition += preguntaLines.length * 5 + 5;

        // Alternativas
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        if (pregunta.alternativas && Array.isArray(pregunta.alternativas)) {
          pregunta.alternativas.forEach((alt: any) => {
            doc.text(`${alt.letra}) ${alt.texto}`, 30, yPosition);
            yPosition += 6;
          });
        }

        yPosition += 5;
      });

      // Guardar PDF
      const nombreArchivo = todas
        ? `Prueba_${asignacionDetalle.prueba.nombre}_${asignacionDetalle.estudiante.nombres}_${asignacionDetalle.estudiante.apellidos}.pdf`
        : `Prueba_${asignacionDetalle.prueba.nombre}.pdf`;
      doc.save(nombreArchivo);
    } catch (error: any) {
      console.error("Error generando PDF:", error);
      showNotification(
        "error",
        "Error al generar PDF",
        error?.response?.data?.message || "No se pudo generar el PDF"
      );
    }
  };

  const generarPDFConsolidado = async () => {
    // Usar asignaciones seleccionadas o todas si no hay selección
    const asignacionesAGenerar =
      asignacionesSeleccionadas.length > 0
        ? asignaciones.filter((a) =>
            asignacionesSeleccionadas.includes(a.idAsignacionPruebaSimce)
          )
        : asignaciones;

    if (asignacionesAGenerar.length === 0) {
      showNotification(
        "warning",
        "Sin asignaciones",
        "No hay asignaciones seleccionadas para generar el PDF consolidado"
      );
      return;
    }

    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;

      // Portada
      doc.setFillColor(139, 92, 246); // purple-600
      doc.rect(0, 0, pageWidth, pageHeight, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(28);
      doc.setFont("helvetica", "bold");
      doc.text(
        "Pruebas SIMCE - Consolidado",
        pageWidth / 2,
        pageHeight / 2 - 20,
        {
          align: "center",
        }
      );
      doc.setFontSize(16);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Total de asignaciones: ${asignacionesAGenerar.length}`,
        pageWidth / 2,
        pageHeight / 2,
        { align: "center" }
      );
      doc.setFontSize(12);
      doc.text(
        `Generado el ${new Date().toLocaleDateString("es-ES")}`,
        pageWidth / 2,
        pageHeight / 2 + 15,
        { align: "center" }
      );

      // Generar una sección por cada asignación
      for (let i = 0; i < asignacionesAGenerar.length; i++) {
        const asignacion = asignacionesAGenerar[i];

        // Obtener el detalle completo de la asignación
        const asignacionDetalle = await simceService.getAsignacionById(
          asignacion.idAsignacionPruebaSimce
        );

        // Nueva página para cada asignación (siempre, para separar de la portada)
        doc.addPage();
        yPosition = 20;

        // Header con datos del estudiante
        doc.setFillColor(139, 92, 246); // purple-600
        doc.rect(0, 0, pageWidth, 60, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text(
          `Asignación ${i + 1} de ${asignacionesAGenerar.length}`,
          20,
          25
        );
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text(
          `Estudiante: ${asignacionDetalle.estudiante.nombres} ${asignacionDetalle.estudiante.apellidos}`,
          20,
          35
        );
        if (asignacionDetalle.estudiante.rut) {
          doc.text(`RUT: ${asignacionDetalle.estudiante.rut}`, 20, 42);
        }
        if (asignacionDetalle.estudiante.cursos.length > 0) {
          const curso = asignacionDetalle.estudiante.cursos[0];
          doc.text(`Curso: ${curso.nombre}`, 20, 49);
          if (curso.colegio) {
            doc.text(`Colegio: ${curso.colegio}`, 20, 56);
          }
        }

        yPosition = 70;

        // Información de la prueba
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(asignacionDetalle.prueba.nombre, 20, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(
          `Asignatura: ${
            ASIGNATURAS_LABELS[asignacionDetalle.prueba.asignatura]
          }`,
          20,
          yPosition
        );
        yPosition += 7;
        doc.text(
          `Nivel: ${NIVELES_LABELS[asignacionDetalle.prueba.nivel]}`,
          20,
          yPosition
        );
        yPosition += 7;
        doc.text(
          `Tiempo total: ${asignacionDetalle.prueba.tiempoTotal} minutos`,
          20,
          yPosition
        );
        yPosition += 7;
        if (asignacionDetalle.fechaLimite) {
          const fechaLimite = new Date(asignacionDetalle.fechaLimite);
          doc.text(
            `Fecha límite: ${fechaLimite.toLocaleDateString("es-ES")}`,
            20,
            yPosition
          );
          yPosition += 7;
        }
        if (asignacionDetalle.asignador) {
          doc.text(
            `Asignado por: ${asignacionDetalle.asignador.nombres} ${asignacionDetalle.asignador.apellidos}`,
            20,
            yPosition
          );
          yPosition += 7;
        }
        yPosition += 5;

        // Preguntas
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Preguntas", 20, yPosition);
        yPosition += 10;

        if (asignacionDetalle.prueba.preguntas) {
          for (let index = 0; index < asignacionDetalle.prueba.preguntas.length; index++) {
            const pregunta = asignacionDetalle.prueba.preguntas[index];
            // Verificar si necesitamos una nueva página
            if (yPosition > pageHeight - 50) {
              doc.addPage();
              yPosition = 20;
            }

            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            const preguntaText = `${index + 1}. ${pregunta.contenido}`;
            const preguntaLines = doc.splitTextToSize(
              preguntaText,
              pageWidth - 40
            );
            doc.text(preguntaLines, 20, yPosition);
            yPosition += preguntaLines.length * 5 + 5;

            // Alternativas
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            if (pregunta.alternativas && Array.isArray(pregunta.alternativas)) {
              for (const alt of pregunta.alternativas) {
                if (yPosition > pageHeight - 30) {
                  doc.addPage();
                  yPosition = 20;
                }
                doc.text(`${alt.letra}) ${alt.texto}`, 30, yPosition);
                yPosition += 6;
              }
            }

            yPosition += 5;
          }
        }

        // Separador entre asignaciones
        if (i < asignacionesAGenerar.length - 1) {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          } else {
            yPosition += 10;
            doc.setDrawColor(200, 200, 200);
            doc.line(20, yPosition, pageWidth - 20, yPosition);
            yPosition += 10;
          }
        }
      }

      // Guardar PDF consolidado
      const nombreArchivo = `Pruebas_SIMCE_Consolidado_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      doc.save(nombreArchivo);

      showNotification(
        "success",
        "PDF consolidado generado",
        `Se generó el PDF consolidado con ${asignacionesAGenerar.length} asignación(es) exitosamente`
      );

      // Limpiar selección
      setAsignacionesSeleccionadas([]);
    } catch (error: any) {
      console.error("Error generando PDF consolidado:", error);
      showNotification(
        "error",
        "Error al generar PDF consolidado",
        error?.response?.data?.message ||
          "No se pudo generar el PDF consolidado"
      );
    }
  };

  const renderVistaAsignaciones = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Asignaciones de Pruebas SIMCE
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona las pruebas asignadas a tus estudiantes
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {asignacionesSeleccionadas.length > 0 && (
            <span className="text-sm text-gray-600 font-medium">
              {asignacionesSeleccionadas.length} seleccionada
              {asignacionesSeleccionadas.length !== 1 ? "s" : ""}
            </span>
          )}
          {asignaciones.length > 0 && (
            <>
              <button
                onClick={() => {
                  if (
                    asignacionesSeleccionadas.length === asignaciones.length
                  ) {
                    setAsignacionesSeleccionadas([]);
                  } else {
                    setAsignacionesSeleccionadas(
                      asignaciones.map((a) => a.idAsignacionPruebaSimce)
                    );
                  }
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {asignacionesSeleccionadas.length === asignaciones.length
                  ? "Deseleccionar todos"
                  : "Seleccionar todos"}
              </button>
              {asignacionesSeleccionadas.length > 0 && (
                <button
                  onClick={() =>
                    confirmarEliminarAsignaciones(asignacionesSeleccionadas)
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Eliminar Seleccionadas ({asignacionesSeleccionadas.length})
                </button>
              )}
              <button
                onClick={generarPDFConsolidado}
                disabled={
                  asignacionesSeleccionadas.length === 0 &&
                  asignaciones.length === 0
                }
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                {asignacionesSeleccionadas.length > 0
                  ? `Descargar PDF Consolidado (${asignacionesSeleccionadas.length})`
                  : "Descargar PDF Consolidado (Todos)"}
              </button>
            </>
          )}
          <button
            onClick={() => setVistaActiva("pruebas")}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg flex items-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Asignar Prueba
          </button>
        </div>
      </div>

      {loadingAsignaciones ? (
        <div className="bg-white rounded-xl shadow-lg p-12 border border-purple-100 text-center">
          <p className="text-gray-500">Cargando asignaciones...</p>
        </div>
      ) : asignaciones.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 border border-purple-100 text-center">
          <div className="flex flex-col items-center justify-center">
            <svg
              className="w-16 h-16 text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No hay asignaciones de momento
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Asigna pruebas a tus estudiantes desde la sección de pruebas
            </p>
            <button
              onClick={() => setVistaActiva("pruebas")}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg flex items-center gap-2 mx-auto"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Ir a Pruebas
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-purple-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-purple-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={
                        asignaciones.length > 0 &&
                        asignacionesSeleccionadas.length === asignaciones.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAsignacionesSeleccionadas(
                            asignaciones.map((a) => a.idAsignacionPruebaSimce)
                          );
                        } else {
                          setAsignacionesSeleccionadas([]);
                        }
                      }}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Estudiante
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Prueba
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Fecha Asignación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Fecha Límite
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {asignaciones.map((asignacion) => (
                  <tr
                    key={asignacion.idAsignacionPruebaSimce}
                    className={`hover:bg-gray-50 ${
                      asignacionesSeleccionadas.includes(
                        asignacion.idAsignacionPruebaSimce
                      )
                        ? "bg-purple-50"
                        : ""
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={asignacionesSeleccionadas.includes(
                          asignacion.idAsignacionPruebaSimce
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAsignacionesSeleccionadas([
                              ...asignacionesSeleccionadas,
                              asignacion.idAsignacionPruebaSimce,
                            ]);
                          } else {
                            setAsignacionesSeleccionadas(
                              asignacionesSeleccionadas.filter(
                                (id) =>
                                  id !== asignacion.idAsignacionPruebaSimce
                              )
                            );
                          }
                        }}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {asignacion.estudiante.nombres}{" "}
                        {asignacion.estudiante.apellidos}
                      </div>
                      <div className="text-sm text-gray-500">
                        {asignacion.estudiante.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {asignacion.prueba.nombre}
                      </div>
                      <div className="text-sm text-gray-500">
                        {
                          ASIGNATURAS_LABELS[
                            asignacion.prueba.asignatura as AsignaturaSimce
                          ]
                        }{" "}
                        -{" "}
                        {NIVELES_LABELS[asignacion.prueba.nivel as NivelSimce]}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(asignacion.fechaAsignacion).toLocaleDateString(
                        "es-ES"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {asignacion.fechaLimite
                        ? new Date(asignacion.fechaLimite).toLocaleDateString(
                            "es-ES"
                          )
                        : "Sin límite"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          ESTADO_ASIGNACION_COLORS[asignacion.estado]
                        }`}
                      >
                        {ESTADO_ASIGNACION_LABELS[asignacion.estado]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => generarPDFAsignacion(asignacion)}
                          className="text-green-600 hover:text-green-900"
                          title="Descargar PDF"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() =>
                            confirmarEliminarAsignacion(
                              asignacion.idAsignacionPruebaSimce
                            )
                          }
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar asignación"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <ProfesorLayout>
      {/* Notificación */}
      {notification.isOpen && (
        <div className="fixed top-4 right-4 z-50 max-w-sm w-full animate-in slide-in-from-top-5">
          <div
            className={`rounded-xl shadow-2xl p-4 border-l-4 ${
              notification.type === "success"
                ? "bg-green-50 border-green-500 text-green-800"
                : notification.type === "error"
                ? "bg-red-50 border-red-500 text-red-800"
                : notification.type === "warning"
                ? "bg-yellow-50 border-yellow-500 text-yellow-800"
                : "bg-blue-50 border-blue-500 text-blue-800"
            }`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {notification.type === "success" && (
                  <svg
                    className="w-6 h-6 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {notification.type === "error" && (
                  <svg
                    className="w-6 h-6 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {notification.type === "warning" && (
                  <svg
                    className="w-6 h-6 text-yellow-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                )}
                {notification.type === "info" && (
                  <svg
                    className="w-6 h-6 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold">{notification.title}</h3>
                <p className="text-sm mt-1 opacity-90">
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() =>
                  setNotification({
                    ...notification,
                    isOpen: false,
                  })
                }
                className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Header con gradiente */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 rounded-2xl shadow-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-16 w-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">SIMCE</h1>
              <p className="text-purple-100 mt-1">
                Sistema de Medición de la Calidad de la Educación
              </p>
            </div>
          </div>
          <p className="text-purple-50 text-sm">
            Gestiona preguntas y crea pruebas personalizadas para evaluar el
            aprendizaje de tus estudiantes
          </p>
        </div>

        {/* Tabs de navegación */}
        <div className="bg-white rounded-xl shadow-lg border border-purple-100 overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setVistaActiva("preguntas")}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                vistaActiva === "preguntas"
                  ? "bg-purple-50 text-purple-700 border-b-2 border-purple-600"
                  : "text-gray-600 hover:text-purple-600 hover:bg-purple-50/50"
              }`}
            >
              Preguntas ({preguntasFiltradas.length})
            </button>
            <button
              onClick={() => setVistaActiva("pruebas")}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                vistaActiva === "pruebas"
                  ? "bg-purple-50 text-purple-700 border-b-2 border-purple-600"
                  : "text-gray-600 hover:text-purple-600 hover:bg-purple-50/50"
              }`}
            >
              Pruebas ({pruebas.length})
            </button>
            <button
              onClick={() => setVistaActiva("asignaciones")}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                vistaActiva === "asignaciones"
                  ? "bg-purple-50 text-purple-700 border-b-2 border-purple-600"
                  : "text-gray-600 hover:text-purple-600 hover:bg-purple-50/50"
              }`}
            >
              Asignaciones ({asignaciones.length})
            </button>
          </div>
        </div>

        {/* Contenido según vista activa */}
        {vistaActiva === "preguntas" && renderVistaPreguntas()}
        {vistaActiva === "crear-pregunta" && renderCrearPregunta()}
        {vistaActiva === "crear-prueba" && renderCrearPrueba()}
        {vistaActiva === "pruebas" && renderVistaPruebas()}
        {vistaActiva === "asignaciones" && renderVistaAsignaciones()}
      </div>

      {/* Modal de detalles de pregunta */}
      {preguntaViendo && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => {
              setPreguntaViendo(null);
              if (pruebaAnterior) {
                setPruebaViendo(pruebaAnterior);
                setPruebaAnterior(null);
              }
            }}
          ></div>

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col overflow-hidden">
              {/* Header del modal */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Detalles de la Pregunta
                    </h2>
                    <p className="text-purple-100 text-sm">
                      Información completa de la pregunta SIMCE
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPreguntaViendo(null);
                    if (pruebaAnterior) {
                      setPruebaViendo(pruebaAnterior);
                      setPruebaAnterior(null);
                    }
                  }}
                  className="text-white hover:text-purple-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Contenido del modal */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                {/* Información básica */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-xs text-purple-600 font-medium mb-1">
                      Asignatura
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {ASIGNATURAS_LABELS[preguntaViendo.asignatura]}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium mb-1">
                      Nivel
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {NIVELES_LABELS[preguntaViendo.nivel]}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-xs text-green-600 font-medium mb-1">
                      Dificultad
                    </p>
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                        DIFICULTAD_COLORS[preguntaViendo.dificultad]
                      }`}
                    >
                      {DIFICULTAD_LABELS[preguntaViendo.dificultad]}
                    </span>
                  </div>
                </div>

                {/* Pregunta */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Pregunta
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-base text-gray-900 leading-relaxed break-words overflow-wrap-anywhere whitespace-pre-wrap">
                      {preguntaViendo.contenido}
                    </p>
                  </div>
                </div>

                {/* Alternativas */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    Alternativas
                  </h3>
                  <div className="space-y-3">
                    {preguntaViendo.alternativas.map((alt) => (
                      <div
                        key={alt.letra}
                        className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all ${
                          alt.esCorrecta
                            ? "bg-green-50 border-green-300 shadow-sm"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div
                          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                            alt.esCorrecta
                              ? "bg-green-500 text-white"
                              : "bg-gray-300 text-gray-700"
                          }`}
                        >
                          {alt.letra}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 break-words overflow-wrap-anywhere">
                            {alt.texto}
                          </p>
                        </div>
                        {alt.esCorrecta && (
                          <div className="flex-shrink-0">
                            <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              Correcta
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Explicación */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Explicación
                  </h3>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-gray-900 leading-relaxed break-words overflow-wrap-anywhere whitespace-pre-wrap">
                      {preguntaViendo.explicacion}
                    </p>
                  </div>
                </div>

                {/* Información adicional */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {preguntaViendo.ejeTematico && (
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <p className="text-xs text-yellow-600 font-medium mb-1">
                        Eje Temático
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {preguntaViendo.ejeTematico}
                      </p>
                    </div>
                  )}
                  {preguntaViendo.habilidad && (
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                      <p className="text-xs text-indigo-600 font-medium mb-1">
                        Habilidad
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {preguntaViendo.habilidad}
                      </p>
                    </div>
                  )}
                </div>

                {/* Metadatos */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Tiempo Estimado</p>
                      <p className="font-semibold text-gray-900">
                        {preguntaViendo.tiempoEstimado} minutos
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Creado por</p>
                      <p className="font-semibold text-gray-900">
                        {preguntaViendo.creador
                          ? `${preguntaViendo.creador.nombres} ${preguntaViendo.creador.apellidos}`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Fecha Creación</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(
                          preguntaViendo.fechaCreacion
                        ).toLocaleDateString("es-CL")}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Estado</p>
                      <span
                        className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${
                          preguntaViendo.vigente
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {preguntaViendo.vigente ? "Vigente" : "No vigente"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer del modal */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => {
                    setPreguntaViendo(null);
                    abrirModalPregunta(preguntaViendo);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Editar Pregunta
                </button>
                <button
                  onClick={() => {
                    setPreguntaViendo(null);
                    if (pruebaAnterior) {
                      setPruebaViendo(pruebaAnterior);
                      setPruebaAnterior(null);
                    }
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalles de prueba */}
      {pruebaViendo && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => {
              setPruebaViendo(null);
              setPruebaAnterior(null);
            }}
          ></div>

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] flex flex-col overflow-hidden">
              {/* Header del modal */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Detalles de la Prueba
                    </h2>
                    <p className="text-purple-100 text-sm">
                      Información completa de la prueba SIMCE
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPruebaViendo(null);
                    setPruebaAnterior(null);
                  }}
                  className="text-white hover:text-purple-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Contenido del modal */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                {/* Información básica */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {pruebaViendo.nombre}
                  </h3>
                  {pruebaViendo.descripcion && (
                    <p className="text-sm text-gray-600 mb-4">
                      {pruebaViendo.descripcion}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-xs text-purple-600 font-medium mb-1">
                      Asignatura
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {ASIGNATURAS_LABELS[pruebaViendo.asignatura]}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium mb-1">
                      Nivel
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {NIVELES_LABELS[pruebaViendo.nivel]}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-xs text-green-600 font-medium mb-1">
                      Preguntas
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {pruebaViendo.preguntasIds.length}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <p className="text-xs text-orange-600 font-medium mb-1">
                      Tiempo Total
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {pruebaViendo.tiempoTotal} min
                    </p>
                  </div>
                </div>

                {/* Preguntas de la prueba */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Preguntas de la Prueba ({pruebaViendo.preguntasIds.length})
                  </h3>
                  <button
                    onClick={() => setMostrarModalAgregarPreguntas(true)}
                    className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 mb-3"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Agregar Preguntas
                  </button>
                  <div className="space-y-4">
                    {preguntas
                      .filter((p) =>
                        pruebaViendo.preguntasIds.includes(p.idPreguntaSimce)
                      )
                      .map((pregunta, index) => (
                        <div
                          key={pregunta.idPreguntaSimce}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-purple-300 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                                  Pregunta {index + 1}
                                </span>
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded border ${
                                    DIFICULTAD_COLORS[pregunta.dificultad]
                                  }`}
                                >
                                  {DIFICULTAD_LABELS[pregunta.dificultad]}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {pregunta.tiempoEstimado} min
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-900 mb-2">
                                {pregunta.contenido}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setPruebaAnterior(pruebaViendo);
                                  setPruebaViendo(null);
                                  setPreguntaViendo(pregunta);
                                }}
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                title="Ver detalles"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={async () => {
                                  if (
                                    window.confirm(
                                      "¿Estás seguro de que deseas eliminar esta pregunta de la prueba?"
                                    )
                                  ) {
                                    try {
                                      const nuevasPreguntasIds =
                                        pruebaViendo.preguntasIds.filter(
                                          (id) =>
                                            id !== pregunta.idPreguntaSimce
                                        );

                                      const pruebaActualizada =
                                        await simceService.updatePrueba({
                                          idPruebaSimce:
                                            pruebaViendo.idPruebaSimce,
                                          preguntasIds: nuevasPreguntasIds,
                                        });
                                      setPruebaViendo(pruebaActualizada);

                                      // Actualizar en la lista de pruebas
                                      setPruebas(
                                        pruebas.map((p) =>
                                          p.idPruebaSimce ===
                                          pruebaViendo.idPruebaSimce
                                            ? pruebaActualizada
                                            : p
                                        )
                                      );

                                      showNotification(
                                        "success",
                                        "Pregunta eliminada",
                                        "La pregunta se eliminó de la prueba exitosamente"
                                      );
                                    } catch (error: any) {
                                      showNotification(
                                        "error",
                                        "Error al eliminar",
                                        error?.response?.data?.message ||
                                          "No se pudo eliminar la pregunta"
                                      );
                                    }
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Eliminar de la prueba"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {pregunta.alternativas.map((alt) => (
                              <div
                                key={alt.letra}
                                className={`text-xs p-2 rounded border ${
                                  alt.esCorrecta
                                    ? "bg-green-50 border-green-300 text-green-900 font-semibold"
                                    : "bg-gray-50 border-gray-200 text-gray-700"
                                }`}
                              >
                                <span className="font-semibold">
                                  {alt.letra}.
                                </span>{" "}
                                {alt.texto}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Metadatos */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Fecha Creación</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(
                          pruebaViendo.fechaCreacion
                        ).toLocaleDateString("es-CL")}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Última Actualización</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(
                          pruebaViendo.fechaActualizacion
                        ).toLocaleDateString("es-CL")}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Estado</p>
                      <span
                        className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${
                          pruebaViendo.vigente
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {pruebaViendo.vigente ? "Vigente" : "No vigente"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer del modal */}
              <div className="bg-gray-50 px-6 py-4 flex justify-between items-center gap-3 border-t border-gray-200">
                <button
                  onClick={() =>
                    confirmarEliminarPrueba(pruebaViendo.idPruebaSimce)
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Eliminar
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => descargarPrueba(pruebaViendo)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Descargar PDF
                  </button>
                  <button
                    onClick={() => {
                      setPruebaViendo(null);
                      setPruebaAnterior(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {confirmacionEliminar.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() =>
              setConfirmacionEliminar({
                isOpen: false,
                tipo: "pregunta",
                id: null,
              })
            }
          ></div>

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
              {/* Header del modal */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Confirmar eliminación
                    </h2>
                    <p className="text-red-100 text-sm">
                      Esta acción no se puede deshacer
                    </p>
                  </div>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="p-6">
                <p className="text-gray-700 mb-2">
                  ¿Estás seguro de que deseas eliminar{" "}
                  {confirmacionEliminar.tipo === "asignaciones"
                    ? `${confirmacionEliminar.cantidad} asignación(es)`
                    : confirmacionEliminar.tipo === "asignacion"
                    ? "esta asignación"
                    : confirmacionEliminar.tipo === "pregunta"
                    ? "esta pregunta"
                    : "esta prueba"}
                  ?
                </p>
                {confirmacionEliminar.nombre && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-gray-200">
                    <p className="text-sm text-gray-600 font-medium">
                      {confirmacionEliminar.tipo === "pregunta"
                        ? "Pregunta:"
                        : confirmacionEliminar.tipo === "asignacion"
                        ? "Asignación:"
                        : "Prueba:"}
                    </p>
                    <p className="text-sm text-gray-900 mt-1">
                      {confirmacionEliminar.nombre}
                    </p>
                  </div>
                )}
                {confirmacionEliminar.tipo === "asignaciones" && (
                  <div className="bg-purple-50 rounded-lg p-3 mb-4 border border-purple-200">
                    <p className="text-sm text-purple-600 font-medium">
                      Cantidad: {confirmacionEliminar.cantidad} asignación(es)
                    </p>
                  </div>
                )}
                <p className="text-sm text-red-600 font-medium">
                  ⚠️ Esta acción es permanente y no se puede revertir.
                </p>
              </div>

              {/* Footer del modal */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl border-t border-gray-200">
                <button
                  onClick={() =>
                    setConfirmacionEliminar({
                      isOpen: false,
                      tipo: "pregunta",
                      id: null,
                    })
                  }
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (confirmacionEliminar.tipo === "pregunta") {
                      eliminarPregunta();
                    } else if (confirmacionEliminar.tipo === "prueba") {
                      eliminarPrueba();
                    } else if (confirmacionEliminar.tipo === "asignacion") {
                      eliminarAsignacion();
                    } else if (confirmacionEliminar.tipo === "asignaciones") {
                      eliminarAsignaciones();
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para asignar prueba */}
      {mostrarModalAsignar && pruebaSeleccionadaParaAsignar && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-black bg-opacity-50 backdrop-blur-sm"
              onClick={() => setMostrarModalAsignar(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header con gradiente */}
              <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <svg
                        className="w-7 h-7 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Asignar Prueba SIMCE
                      </h3>
                      <p className="text-sm text-purple-100 mt-0.5">
                        {pruebaSeleccionadaParaAsignar.nombre}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setMostrarModalAsignar(false)}
                    className="text-white hover:text-purple-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="bg-white px-6 py-6">
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        Seleccionar Estudiantes *
                      </label>
                      {estudiantes.length > 0 && (
                        <button
                          onClick={() => {
                            if (
                              estudiantesSeleccionados.length ===
                              estudiantes.length
                            ) {
                              setEstudiantesSeleccionados([]);
                            } else {
                              setEstudiantesSeleccionados(
                                estudiantes.map((e) => e.idUsuario)
                              );
                            }
                          }}
                          className="text-sm font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1 transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {estudiantesSeleccionados.length ===
                          estudiantes.length
                            ? "Deseleccionar todos"
                            : "Seleccionar todos"}
                        </button>
                      )}
                    </div>
                    <div className="border-2 border-purple-200 rounded-xl p-4 max-h-80 overflow-y-auto bg-gradient-to-b from-purple-50/50 to-white">
                      {estudiantes.length === 0 ? (
                        <div className="text-center py-8">
                          <svg
                            className="w-12 h-12 text-gray-400 mx-auto mb-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                          <p className="text-sm text-gray-500">
                            No hay estudiantes disponibles
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {estudiantes.map((estudiante) => (
                            <label
                              key={estudiante.idUsuario}
                              className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all ${
                                estudiantesSeleccionados.includes(
                                  estudiante.idUsuario
                                )
                                  ? "bg-purple-100 border-2 border-purple-400 shadow-sm"
                                  : "bg-white border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={estudiantesSeleccionados.includes(
                                  estudiante.idUsuario
                                )}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEstudiantesSeleccionados([
                                      ...estudiantesSeleccionados,
                                      estudiante.idUsuario,
                                    ]);
                                  } else {
                                    setEstudiantesSeleccionados(
                                      estudiantesSeleccionados.filter(
                                        (id) => id !== estudiante.idUsuario
                                      )
                                    );
                                  }
                                }}
                                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 cursor-pointer"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-gray-900">
                                  {estudiante.nombres} {estudiante.apellidos}
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {estudiante.email}
                                </div>
                              </div>
                              {estudiantesSeleccionados.includes(
                                estudiante.idUsuario
                              ) && (
                                <svg
                                  className="w-5 h-5 text-purple-600"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {estudiantesSeleccionados.length > 0 && (
                      <p className="mt-2 text-xs text-purple-600 font-medium">
                        {estudiantesSeleccionados.length} estudiante
                        {estudiantesSeleccionados.length !== 1 ? "s" : ""}{" "}
                        seleccionado
                        {estudiantesSeleccionados.length !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      Fecha Límite (Opcional)
                    </label>
                    <input
                      type="datetime-local"
                      value={
                        fechaLimiteAsignacion
                          ? new Date(fechaLimiteAsignacion)
                              .toISOString()
                              .slice(0, 16)
                          : ""
                      }
                      onChange={(e) =>
                        setFechaLimiteAsignacion(
                          e.target.value
                            ? new Date(e.target.value).toISOString()
                            : null
                        )
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-gray-50 to-purple-50 px-6 py-4 flex flex-col sm:flex-row sm:justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={() => setMostrarModalAsignar(false)}
                  className="w-full sm:w-auto inline-flex justify-center items-center gap-2 rounded-xl border-2 border-gray-300 shadow-sm px-5 py-2.5 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Cancelar
                </button>
                <button
                  onClick={asignarPrueba}
                  disabled={estudiantesSeleccionados.length === 0}
                  className="w-full sm:w-auto inline-flex justify-center items-center gap-2 rounded-xl border border-transparent shadow-lg px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-sm font-semibold text-white hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-purple-600 disabled:hover:to-indigo-600 transition-all transform hover:scale-105"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Asignar Prueba
                  {estudiantesSeleccionados.length > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                      {estudiantesSeleccionados.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar preguntas a la prueba */}
      {mostrarModalAgregarPreguntas && pruebaViendo && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-black bg-opacity-50 backdrop-blur-sm"
              onClick={() => setMostrarModalAgregarPreguntas(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <svg
                        className="w-7 h-7 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Agregar Preguntas a la Prueba
                      </h3>
                      <p className="text-sm text-purple-100 mt-0.5">
                        {pruebaViendo.nombre}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setMostrarModalAgregarPreguntas(false)}
                    className="text-white hover:text-purple-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="bg-white px-6 py-6">
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Selecciona las preguntas que deseas agregar a esta prueba.
                    Solo se muestran preguntas compatibles con la asignatura y
                    nivel de la prueba.
                  </p>
                  <div className="border-2 border-purple-200 rounded-xl p-4 max-h-96 overflow-y-auto bg-gradient-to-b from-purple-50/50 to-white">
                    {preguntas.filter(
                      (p) =>
                        p.asignatura === pruebaViendo.asignatura &&
                        p.nivel === pruebaViendo.nivel &&
                        !pruebaViendo.preguntasIds.includes(p.idPreguntaSimce)
                    ).length === 0 ? (
                      <div className="text-center py-8">
                        <svg
                          className="w-12 h-12 text-gray-400 mx-auto mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-sm text-gray-500">
                          No hay preguntas disponibles para agregar
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {preguntas
                          .filter(
                            (p) =>
                              p.asignatura === pruebaViendo.asignatura &&
                              p.nivel === pruebaViendo.nivel &&
                              !pruebaViendo.preguntasIds.includes(
                                p.idPreguntaSimce
                              )
                          )
                          .map((pregunta) => (
                            <div
                              key={pregunta.idPreguntaSimce}
                              className="bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-purple-300 transition-all"
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  id={`pregunta-${pregunta.idPreguntaSimce}`}
                                  className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                                  onChange={async (e) => {
                                    if (e.target.checked) {
                                      try {
                                        const nuevasPreguntasIds = [
                                          ...pruebaViendo.preguntasIds,
                                          pregunta.idPreguntaSimce,
                                        ];

                                        const pruebaActualizada =
                                          await simceService.updatePrueba({
                                            idPruebaSimce:
                                              pruebaViendo.idPruebaSimce,
                                            preguntasIds: nuevasPreguntasIds,
                                          });

                                        setPruebaViendo(pruebaActualizada);
                                        setPruebas(
                                          pruebas.map((p) =>
                                            p.idPruebaSimce ===
                                            pruebaViendo.idPruebaSimce
                                              ? pruebaActualizada
                                              : p
                                          )
                                        );

                                        showNotification(
                                          "success",
                                          "Pregunta agregada",
                                          "La pregunta se agregó a la prueba exitosamente"
                                        );
                                      } catch (error: any) {
                                        e.target.checked = false;
                                        showNotification(
                                          "error",
                                          "Error al agregar",
                                          error?.response?.data?.message ||
                                            "No se pudo agregar la pregunta"
                                        );
                                      }
                                    }
                                  }}
                                />
                                <label
                                  htmlFor={`pregunta-${pregunta.idPreguntaSimce}`}
                                  className="flex-1 cursor-pointer"
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <span
                                      className={`px-2 py-1 text-xs font-semibold rounded border ${
                                        DIFICULTAD_COLORS[pregunta.dificultad]
                                      }`}
                                    >
                                      {DIFICULTAD_LABELS[pregunta.dificultad]}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {pregunta.tiempoEstimado} min
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {pregunta.contenido}
                                  </p>
                                </label>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-gray-50 to-purple-50 px-6 py-4 flex justify-end border-t border-gray-200">
                <button
                  onClick={() => setMostrarModalAgregarPreguntas(false)}
                  className="px-5 py-2.5 bg-white border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ProfesorLayout>
  );
};
